import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import type { Blackout, CalendarLeave } from '@lms/shared-types'
import { HIDDEN_LEAVE_TYPE, leaveTypeStyle } from '../../lib/leave-type-palette'
import type { TeamColorStyle } from '../../lib/team-color-palette'
import { fmtDateWithWeekday } from '../../lib/format'
import {
  LeaveDayBreakdownFromCalendar,
  leaveSupportsBreakdown,
} from '../employee/LeaveDayBreakdown'
import { Card } from './Card'
import { Icon } from './Icon'

interface CalendarDayPopoverProps {
  date: string
  leaves: ReadonlyArray<CalendarLeave>
  publicHoliday?: { name: string }
  blackouts: ReadonlyArray<Blackout>
  /** Coverage warning — top-edge ribbon on the cell, with per-team breakdown. */
  coverage?: {
    level: 'medium' | 'high'
    teams: ReadonlyArray<{
      teamName: string
      absentCount: number
      headcount: number
      ratio: number
    }>
  }
  open: boolean
  onClose: () => void
  anchorRef: RefObject<HTMLElement>
  onLeaveClick?: (leaveId: string) => void
  /** When provided, restricts which leave bars are clickable. Called per-leave;
   *  returning false renders the bar as non-interactive even if onLeaveClick is set. */
  canClickLeave?: (leave: CalendarLeave) => boolean
  /** Colours each person row by their team so the popover matches the calendar
   *  bars (which are team-coloured). Omit to fall back to leave-type colours. */
  teamColor?: (teamId: string) => TeamColorStyle
}

const POPOVER_WIDTH = 320
const POPOVER_MAX_HEIGHT = 360
const VIEWPORT_MARGIN = 8

// rAF polyfill: JSDOM provides requestAnimationFrame in most modern test
// setups, but guard to be safe.
const raf =
  typeof window !== 'undefined' &&
  typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame.bind(window)
    : (cb: FrameRequestCallback): number => {
        const handle = setTimeout(() => cb(performance.now()), 0)
        return handle as unknown as number
      }
const cancelRaf =
  typeof window !== 'undefined' &&
  typeof window.cancelAnimationFrame === 'function'
    ? window.cancelAnimationFrame.bind(window)
    : (handle: number): void => clearTimeout(handle as unknown as NodeJS.Timeout)

/** "10/Aug" from an ISO "YYYY-MM-DD", read as a local calendar day. */
function fmtDayMonth(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return format(new Date(y, m - 1, d), 'dd/MMM')
}

function formatDateHeader(iso: string): string {
  const parts = iso.split('-').map(Number)
  const y = parts[0]
  const m = parts[1]
  const d = parts[2]
  if (!y || !m || !d) return iso
  // Construct as local midnight so date-fns format() reads the intended
  // calendar day regardless of viewer timezone.
  const date = new Date(y, m - 1, d)
  return fmtDateWithWeekday(date)
}

function bannerStyle(bg: string, fg: string): CSSProperties {
  return {
    background: bg,
    color: fg,
    padding: '6px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  }
}

export function CalendarDayPopover({
  date,
  leaves,
  publicHoliday,
  blackouts,
  coverage,
  open,
  onClose,
  anchorRef,
  onLeaveClick,
  canClickLeave,
  teamColor,
}: Readonly<CalendarDayPopoverProps>): JSX.Element | null {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  // Two-panel slide: null = people list (Panel A); a leave id = that person's
  // day-by-day breakdown (Panel B). The track slides left to reveal Panel B.
  const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null)
  const panelARef = useRef<HTMLDivElement | null>(null)
  const panelBRef = useRef<HTMLDivElement | null>(null)
  const backButtonRef = useRef<HTMLButtonElement | null>(null)
  const prevSelectedRef = useRef<string | null>(null)
  const [panelHeights, setPanelHeights] = useState<{ a: number; b: number }>({ a: 0, b: 0 })

  // Two-phase positioning using measured height so the flip-above math uses
  // the popover's actual content height, not the 360px max constant.
  //
  // Phase 1: On open, render the popover off-screen (opacity: 0, top: -9999)
  //          so the DOM can measure its real dimensions.
  // Phase 2: After the off-screen render, measure and reposition to the
  //          correct location — all before the next paint (useLayoutEffect).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      // Always reopen on the people list, never mid-slide from a prior open.
      setSelectedLeaveId(null)
      return
    }
    const anchor = anchorRef.current
    if (!anchor) return

    // Phase 1: place off-screen so React mounts the popover into the DOM.
    setPos({ top: -9999, left: -9999 })

    // Phase 2: measure + reposition in the next animation frame, after the
    // off-screen render has been committed.
    const handle = raf(() => {
      const popover = popoverRef.current
      if (!popover) return
      const anchorRect = anchor.getBoundingClientRect()
      const popoverHeight = popover.offsetHeight
      const popoverWidth = popover.offsetWidth

      // Clamp width: never exceed the viewport.
      const maxWidth = window.innerWidth - VIEWPORT_MARGIN * 2
      const effectiveWidth = Math.min(popoverWidth, maxWidth)

      let left = anchorRect.left
      let top = anchorRect.bottom + VIEWPORT_MARGIN

      // Horizontal: shift left when overflowing the right edge.
      if (left + effectiveWidth > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(
          VIEWPORT_MARGIN,
          window.innerWidth - effectiveWidth - VIEWPORT_MARGIN,
        )
      }
      if (left < VIEWPORT_MARGIN) {
        left = VIEWPORT_MARGIN
      }

      // Vertical: flip above when overflowing the bottom edge.
      // BUG FIX: use the actual measured height, not POPOVER_MAX_HEIGHT.
      if (top + popoverHeight > window.innerHeight - VIEWPORT_MARGIN) {
        top = anchorRect.top - popoverHeight - VIEWPORT_MARGIN
      }
      // If the flipped-above position also overflows the top edge (popover is
      // taller than the available space above), clamp to the viewport top and
      // let the popover's overflow: auto handle scrolling internally.
      if (top < VIEWPORT_MARGIN) {
        top = VIEWPORT_MARGIN
      }

      setPos({ top: top + window.scrollY, left: left + window.scrollX })
    })

    return () => {
      cancelRaf(handle)
    }
  }, [open, anchorRef])

  // Esc + outside-click handling.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose()
    }
    // Grace period: ignore the very first mousedown so the click that
    // opened the popover doesn't immediately close it.
    let armed = false
    const armHandle = window.setTimeout(() => {
      armed = true
    }, 20)
    function onMouseDown(e: MouseEvent): void {
      if (!armed) return
      const popover = document.querySelector('[data-testid="day-popover"]')
      if (popover && !popover.contains(e.target as Node)) onClose()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onMouseDown)
    return () => {
      window.clearTimeout(armHandle)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onMouseDown)
    }
  }, [open, onClose])

  const grouped = useMemo(() => {
    const map = new Map<string, { teamName: string; leaves: CalendarLeave[] }>()
    for (const l of leaves) {
      const entry = map.get(l.teamId) ?? { teamName: l.teamName, leaves: [] }
      entry.leaves.push(l)
      map.set(l.teamId, entry)
    }
    return [...map.values()].sort((a, b) =>
      a.teamName.localeCompare(b.teamName),
    )
  }, [leaves])

  const selectedLeave =
    selectedLeaveId !== null
      ? (leaves.find((l) => l.id === selectedLeaveId) ?? null)
      : null

  // Track each panel's natural height so the popover animates to the ACTIVE
  // panel's height as it slides. A ResizeObserver keeps this correct when the
  // breakdown panel grows after its lazy detail fetch resolves.
  useLayoutEffect(() => {
    const a = panelARef.current
    const b = panelBRef.current
    if (a === null || b === null) return
    const measure = (): void => {
      setPanelHeights({ a: a.offsetHeight, b: b.offsetHeight })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(a)
    observer.observe(b)
    return () => observer.disconnect()
    // `pos` is included because the panels only mount once positioning resolves
    // (the component returns null while pos === null); without it the measure
    // effect runs against null refs and the absolute panels never get a height.
  }, [open, selectedLeaveId, leaves, pos])

  // Move focus with the slide so the keyboard user follows the visible panel:
  // into the back button on entering the breakdown, back to the person row on
  // returning. This also keeps focus out of the aria-hidden offscreen panel.
  useEffect(() => {
    if (!open) return
    const prev = prevSelectedRef.current
    if (selectedLeaveId !== null && prev === null) {
      backButtonRef.current?.focus()
    } else if (selectedLeaveId === null && prev !== null) {
      document
        .querySelector<HTMLButtonElement>(`[data-testid="day-popover-leave-${prev}"]`)
        ?.focus()
    }
    prevSelectedRef.current = selectedLeaveId
  }, [selectedLeaveId, open])

  if (!open || pos === null) return null

  // Compute effective width at render time (SSR-safe).
  const effectiveWidth =
    typeof window !== 'undefined'
      ? Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2)
      : POPOVER_WIDTH

  // Hide during the off-screen measurement phase so the intermediate
  // top: -9999 position never flashes to the user.
  const isPositioning = pos.top === -9999

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        width: effectiveWidth,
        zIndex: 100,
        opacity: isPositioning ? 0 : 1,
        transition: isPositioning ? 'none' : 'opacity 80ms ease-out',
      }}
    >
      <Card
        data-testid="day-popover"
        role="dialog"
        aria-modal="false"
        style={{
          maxHeight: POPOVER_MAX_HEIGHT,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxShadow: 'var(--shadow-modal)',
        }}
        padding={14}
      >
        {/* Two-panel slider: people list (A) ⇄ per-person day breakdown (B).
            Both panels are absolutely pinned to the clip's left edge at
            width:100%, so a translateX(±100%) is exactly one clip width — the
            active panel always aligns to the clip edge, and the clip animates
            to the active panel's measured height. */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            // Keep the clip at its measured panel height inside the flex column.
            // Without flexShrink:0 the flex parent shrinks this fixed-height clip
            // to fit maxHeight, and — because overflow is hidden — the extra rows
            // are clipped with no scrollbar. Pinning it forces the Card
            // (maxHeight + overflow:auto) to be the real scroll container.
            flexShrink: 0,
            height:
              (selectedLeave !== null ? panelHeights.b : panelHeights.a) || 'auto',
            transition: 'height var(--motion-duration-slow) var(--motion-ease-out)',
          }}
        >
          {/* ── Panel A: people out on this day ── */}
          <div
            ref={panelARef}
            data-testid="day-popover-panel-list"
            aria-hidden={selectedLeave !== null ? true : undefined}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform:
                selectedLeave !== null ? 'translateX(-100%)' : 'translateX(0)',
              transition: 'transform var(--motion-duration-slow) var(--motion-ease-out)',
              pointerEvents: selectedLeave !== null ? 'none' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}
              >
                <h3 style={{ margin: 0, fontSize: 15 }}>{formatDateHeader(date)}</h3>
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                  {leaves.length} {leaves.length === 1 ? 'person' : 'people'} out
                </span>
              </div>

              {publicHoliday !== undefined ? (
                <div
                  style={{
                    // Match the weekend/holiday treatment elsewhere — muted
                    // sunken grey, not amber warning.
                    ...bannerStyle('var(--surface-2)', 'var(--text-2)'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Icon name="event" size={16} />
                  {publicHoliday.name}
                </div>
              ) : null}

              {blackouts.length > 0 ? (
                <div style={bannerStyle('var(--danger-bg)', 'var(--danger-fg)')}>
                  ⊘ Blackout: {blackouts.map((b) => b.name).join(', ')}
                </div>
              ) : null}

              {coverage !== undefined ? (
                <div
                  data-testid="day-popover-coverage"
                  data-level={coverage.level}
                  style={{
                    ...bannerStyle(
                      coverage.level === 'high' ? 'var(--danger-bg)' : 'var(--warning-bg)',
                      coverage.level === 'high' ? 'var(--danger-fg)' : 'var(--warning-fg)',
                    ),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="groups" size={16} />
                    {coverage.level === 'high' ? 'Coverage critical' : 'Coverage at risk'}
                  </div>
                  {coverage.teams.length > 0 ? (
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: 22,
                        fontWeight: 500,
                        fontSize: 11,
                        lineHeight: 1.4,
                      }}
                    >
                      {coverage.teams.map((t) => (
                        <li key={t.teamName}>
                          {t.teamName} · {t.absentCount}/{t.headcount} out (
                          {Math.round(t.ratio * 100)}%)
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {grouped.map((g) => (
                <div key={g.teamName}>
                  <div
                    style={{
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--text-3)',
                      marginBottom: 4,
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono, inherit)',
                    }}
                  >
                    {g.teamName}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {g.leaves.map((l) => {
                      // Colour the chip by team (matching the calendar bars) when
                      // a teamColor resolver is supplied; keep the leave-type
                      // label text either way. Falls back to leave-type colours.
                      const typeStyle = leaveTypeStyle(l.type)
                      const palette =
                        teamColor !== undefined ? teamColor(l.teamId) : typeStyle
                      // A row expands into Panel B when there's a day-by-day
                      // breakdown to show (available to anyone who sees the
                      // leave) OR the viewer may open the full detail. The
                      // in-popover breakdown is decoupled from the open-detail
                      // permission so peers can still drill into the days.
                      const canOpenDetail =
                        onLeaveClick !== undefined &&
                        (canClickLeave === undefined || canClickLeave(l))
                      const interactive = leaveSupportsBreakdown(l) || canOpenDetail
                      const isPending = l.status === 'pending'
                      return (
                        <button
                          key={l.id}
                          data-testid={`day-popover-leave-${l.id}`}
                          data-status={l.status}
                          type="button"
                          // A clickable row slides to that person's day-by-day
                          // breakdown (Panel B) rather than navigating away.
                          onClick={interactive ? () => setSelectedLeaveId(l.id) : undefined}
                          tabIndex={selectedLeave !== null ? -1 : undefined}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 8px',
                            borderRadius: 6,
                            border: `1px ${isPending ? 'dashed' : 'solid'} ${palette.line}`,
                            background: palette.bg,
                            color: palette.ink,
                            opacity: isPending ? 0.7 : 1,
                            cursor: interactive ? 'pointer' : 'default',
                            // Match the calendar bar type size (10px) so the
                            // popover rows read as the same scale as the grid.
                            fontSize: 10,
                            textAlign: 'left',
                            fontFamily: 'inherit',
                          }}
                        >
                          <span style={{ fontWeight: 700 }}>{l.employeeName}</span>
                          {/* Suppress the generic "Leave" label when the viewer
                              isn't allowed to see the leave type — the name alone
                              reads cleaner than "· Leave". */}
                          {l.type !== HIDDEN_LEAVE_TYPE ? (
                            <span style={{ opacity: 0.7 }}>· {typeStyle.label}</span>
                          ) : null}
                          {isPending ? (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                color: 'var(--text-3)',
                              }}
                            >
                              · pending
                            </span>
                          ) : null}
                          <span style={{ opacity: 0.7, marginLeft: 'auto' }}>
                            {fmtDayMonth(l.startDate)} – {fmtDayMonth(l.endDate)}
                          </span>
                          {/* Affordance that the row drills into a breakdown. */}
                          {interactive ? (
                            <Icon name="chevron_right" size={16} color="var(--text-3)" />
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

          {/* ── Panel B: selected person's day-by-day breakdown ── */}
          <div
            ref={panelBRef}
            data-testid="day-popover-panel-breakdown"
            aria-hidden={selectedLeave === null ? true : undefined}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform:
                selectedLeave !== null ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform var(--motion-duration-slow) var(--motion-ease-out)',
              pointerEvents: selectedLeave !== null ? 'auto' : 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
              {selectedLeave !== null ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      ref={backButtonRef}
                      type="button"
                      data-testid="day-popover-back"
                      onClick={() => setSelectedLeaveId(null)}
                      aria-label="Back to people list"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        background: 'var(--surface)',
                        color: 'var(--text-2)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="arrow_back" size={16} />
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: 'var(--text)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedLeave.employeeName}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        {leaveTypeStyle(selectedLeave.type).label} ·{' '}
                        {selectedLeave.startDate.slice(5).replace('-', '/')} –{' '}
                        {selectedLeave.endDate.slice(5).replace('-', '/')}
                      </div>
                    </div>
                  </div>

                  {leaveSupportsBreakdown(selectedLeave) ? (
                    // Cap the day list and let it scroll so a long leave
                    // (e.g. a multi-week continuous leave) never overflows the
                    // popover. The header + "View full details" stay pinned.
                    <div
                      data-testid="day-popover-breakdown-scroll"
                      style={{
                        maxHeight: 220,
                        overflowY: 'auto',
                        // Room for the scrollbar so rows don't sit under it.
                        marginRight: -4,
                        paddingRight: 4,
                      }}
                    >
                      <LeaveDayBreakdownFromCalendar leave={selectedLeave} />
                    </div>
                  ) : null}

                  {onLeaveClick !== undefined &&
                  (canClickLeave === undefined || canClickLeave(selectedLeave)) ? (
                    <button
                      type="button"
                      data-testid="day-popover-view-full"
                      onClick={() => onLeaveClick(selectedLeave.id)}
                      style={{
                        alignSelf: 'flex-start',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 8px',
                        border: 'none',
                        background: 'none',
                        color: 'var(--steel-blue)',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      View full details
                      <Icon name="arrow_forward" size={14} />
                    </button>
                  ) : null}
                </>
              ) : null}
          </div>
        </div>
      </Card>
    </div>,
    document.body,
  )
}
